/**
 * Putting the body in the room: the placement state machine.
 *
 * Pure, so the whole flow (offer, scan, aim, confirm, adjust, anchor) can be
 * driven in tests without a headset, an AR session or a hit-test source. The
 * bridge feeds it session and hit-test events and applies whatever pose it
 * reports; it holds no state of its own.
 *
 *   idle ──offer──▶ requesting ──granted──▶ scanning ──surface──▶ aiming
 *     ▲                  │                     ▲                    │
 *     │                  └─denied──────────────┼───lost─────────────┘
 *     │                                        │                  confirm
 *     └────────────────── exit ────────────────┴──────────────▼
 *                                                          placed ──adjust──▶ aiming
 */
import type { Vec3 } from "./two-hand-transform.js";

export type PlacementPhase = "idle" | "requesting" | "scanning" | "aiming" | "placed" | "unsupported";

export interface PlacementPose {
  position: Vec3;
  yaw: number;
}

export interface PlacementState {
  phase: PlacementPhase;
  /** The live reticle pose while aiming, or the anchored pose once placed. */
  pose: PlacementPose | null;
  /** Set when a request was refused or a session ended unexpectedly. */
  note: string | null;
  /** True once the body has been placed at least once this session. */
  everPlaced: boolean;
}

export type PlacementEvent =
  | { type: "offer" }
  | { type: "granted" }
  | { type: "denied"; reason: string }
  | { type: "unsupported"; reason: string }
  | { type: "surface"; pose: PlacementPose }
  | { type: "lost" }
  | { type: "confirm" }
  | { type: "adjust" }
  | { type: "exit" };

export function initialPlacement(): PlacementState {
  return { phase: "idle", pose: null, note: null, everPlaced: false };
}

/**
 * One transition. Unknown events for a phase leave the state untouched, so a
 * late hit-test result after the user has confirmed cannot move a placed body.
 */
export function placementReducer(state: PlacementState, event: PlacementEvent): PlacementState {
  switch (event.type) {
    case "offer":
      return state.phase === "idle" || state.phase === "unsupported" ? { ...state, phase: "requesting", note: null } : state;

    case "granted":
      return state.phase === "requesting" ? { ...state, phase: "scanning", note: null } : state;

    case "denied":
      return state.phase === "requesting" ? { ...state, phase: "idle", note: event.reason } : state;

    case "unsupported":
      return { ...state, phase: "unsupported", note: event.reason };

    case "surface":
      // a surface only moves the reticle while the user is still choosing
      return state.phase === "scanning" || state.phase === "aiming" ? { ...state, phase: "aiming", pose: event.pose, note: null } : state;

    case "lost":
      return state.phase === "aiming" ? { ...state, phase: "scanning", pose: null } : state;

    case "confirm":
      return state.phase === "aiming" && state.pose ? { ...state, phase: "placed", everPlaced: true, note: null } : state;

    case "adjust":
      return state.phase === "placed" ? { ...state, phase: "scanning", pose: null } : state;

    case "exit":
      // leaving AR keeps `everPlaced` so the client can offer "put it back"
      return { ...state, phase: "idle", pose: null, note: null };

    default:
      return state;
  }
}

/** What the reticle and prompts should say, so the UI has no logic of its own. */
export function placementPrompt(state: PlacementState): string {
  switch (state.phase) {
    case "idle":
      return state.note ?? (state.everPlaced ? "Place the body again" : "Place the body in your room");
    case "requesting":
      return "Asking for permission to see your room…";
    case "scanning":
      return "Look at the floor or a table";
    case "aiming":
      return "Tap to place the body here";
    case "placed":
      return "Walk around it. Tap and hold to move it.";
    case "unsupported":
      return state.note ?? "This headset cannot place things in your room";
  }
}

/** True while the reticle should be drawn. */
export function showsReticle(state: PlacementState): boolean {
  return state.phase === "aiming";
}

/**
 * Turn a placed body to face the viewer, which is what a person expects after
 * walking round to the other side and tapping *face me*.
 */
export function faceViewer(pose: PlacementPose, viewer: Vec3): PlacementPose {
  return { ...pose, yaw: Math.atan2(viewer.x - pose.position.x, viewer.z - pose.position.z) };
}
